import { useCallback, useMemo } from "react";
import { Document, Font, Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import TableColPdf from "components/pdf/TableColPdf";
import { QuoteDetail } from "features/quotation/types";
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatWheelRimDiamDisplay,
  isPriceLabel,
} from "utils";
import TableEstPreviewPdf from "./TableEstPreviewPdf";

/** Render price with ¥ on the left and value centered in the cell for PDF */
function renderPriceWithYen(value: string) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: 2,
        paddingHorizontal: 4,
        width: "100%",
      }}
    >
      <Text style={{ fontSize: 7 }}>¥</Text>
      <Text style={{ fontSize: 7, flex: 1, textAlign: "center" }}>{value}</Text>
      {/* Empty view to balance the ¥ symbol for true center alignment */}
      <View style={{ width: 7 }} />
    </View>
  );
}

Font.register({
  family: "irohakakuC",
  src: "/fonts/irohakakuC-Regular.ttf",
});
Font.registerHyphenationCallback((word) => [word, ""]);

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: "irohakakuC",
    fontSize: 12,
    color: "#000000",
  },
  header: { flexDirection: "row", marginBottom: 8 },
  headerInfo: { fontSize: 13, fontWeight: "bold" },
  headerLabel: { paddingLeft: 5, paddingRight: 5, minWidth: 50 },
  infoRow: {
    display: "flex",
    flexDirection: "row",
    gap: 2,
  },
  containerCarInfo: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottom: "1px solid #707070",
    paddingBottom: 12,
  },
  carInfo: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
  },
  containerDiscount: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 1,
  },
  carTitle: {
    fontSize: 14,
    fontWeight: "medium",
    textAlign: "center",
    color: "#333",
    marginBottom: 10,
  },
  containerQR: {
    display: "flex",
    flexDirection: "row",
  },
  specsCar: {
    display: "flex",
    flexDirection: "column",
    width: 120,
    marginTop: 10,
  },
  priceNote: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  footer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginTop: 30,
  },
  footerText: { fontSize: 10 },
});

/**
 * PreviewPdfEst Component
 *
 * Renders a PDF document preview for a car model quotation using @react-pdf/renderer.
 * Displays store and customer information, car details, pricing breakdown, and a QR code.
 * Uses custom table components for structured data presentation.
 * @param {object} props - Component props.
 * @param {QuoteDetail} props.quotationDetail - The main quotation detail to display.
 * @param {string} props.qrCodeData - Base64 string for the QR code image.
 * The rendered PDF document.
 */
export default function PreviewPdfEst({
  quotationDetail,
  qrCodeData,
  homeQrImageSrc,
}: {
  quotationDetail: QuoteDetail;
  qrCodeData: string;
  homeQrImageSrc?: string;
}) {
  const hasTireData = useMemo<boolean>(
    () =>
      Boolean(
        quotationDetail.brnd_name &&
        quotationDetail.modl_code &&
        quotationDetail.modl_period_s &&
        quotationDetail.modl_period_e,
      ),
    [quotationDetail],
  );

  const prunctureFlagRender = (flag?: QuoteDetail["tire_puncture_flag"]): string => {
    switch (flag) {
      case 2:
        return "2本補償";
      case 4:
        return "4本補償";
      default:
        return "補償なし";
    }
  };

  const tireSizeRender = useMemo(() => {
    if (
      !quotationDetail ||
      !quotationDetail.tbl_quote_detail_tire ||
      quotationDetail.tbl_quote_detail_tire.length === 0
    )
      return "";

    const firstTire = quotationDetail.tbl_quote_detail_tire[0];

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { midt_tire_width, midt_tire_aspect_ratio, midt_tire_rim_diam } = firstTire;
    if (!midt_tire_width || !midt_tire_aspect_ratio || !midt_tire_rim_diam) return "";

    return `${midt_tire_width}/${midt_tire_aspect_ratio}R${midt_tire_rim_diam}`;
  }, [quotationDetail?.tbl_quote_detail_tire]);

  const renderCostValue = useCallback(
    (cost: number | null, flag: number | null) => {
      if (flag === null || flag === 0) return "-";
      if (flag && (cost === 0 || cost === null)) return "無料";
      return formatCurrency(cost as number);
    },
    [quotationDetail],
  );

  // Discount row values for price columns (to support ¥ + value with space-between)
  const discReplac = formatCurrency(quotationDetail.replac_labor_cost);
  const discWaste = renderCostValue(
    quotationDetail.waste_tire_cost,
    quotationDetail.waste_tire_cost_flag,
  );
  const discRubber = renderCostValue(
    quotationDetail.rubber_stem_valve_cost,
    quotationDetail.rubber_stem_valve_flag,
  );
  const discNitrogen = renderCostValue(
    quotationDetail.nitrogen_inflation_cost,
    quotationDetail.nitrogen_inflation_flag,
  );
  const discHub = renderCostValue(
    quotationDetail.hub_rust_prevention_cost,
    quotationDetail.hub_rust_prevention_flag,
  );

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* Header */}
        <View style={{ ...styles.header, justifyContent: "space-between" }}>
          <View>
            <View style={{ display: "flex", flexDirection: "row" }}>
              <Text style={{ ...styles.headerInfo, ...styles.headerLabel }}>店舗名</Text>
              <Text style={styles.headerInfo}>：{quotationDetail?.store_name}</Text>
            </View>
            <View style={{ display: "flex", flexDirection: "row" }}>
              <Text style={{ ...styles.headerInfo, ...styles.headerLabel }}>住所</Text>
              <Text style={styles.headerInfo}>：{quotationDetail?.address}</Text>
            </View>
            <View style={{ display: "flex", flexDirection: "row" }}>
              <Text style={{ ...styles.headerInfo, ...styles.headerLabel }}>電話</Text>
              <Text style={styles.headerInfo}>
                ：{quotationDetail?.tel_no} &nbsp;&nbsp;&nbsp; 見積担当：
                {quotationDetail?.quote_by}
              </Text>
            </View>
          </View>
          <View style={{ alignItems: "flex-end" }}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
              <Text style={{ fontSize: 8 }}>
                当社グループは個人情報保護法に準拠し、適切な個人情報の取り扱いをしております。{"\n"}
                プライバシーポリシーは当社HPに記載及び店頭に掲示しております。
              </Text>
              <Image
                src={homeQrImageSrc || "/img/home_qr_code.png"}
                style={{ width: 45, height: 45 }}
              />
            </View>
          </View>
        </View>
        {/* car info */}
        <View style={{ ...styles.containerCarInfo, flexDirection: "column" }}>
          <View style={{ display: "flex", flexDirection: "row", marginTop: 14 }}>
            <View style={{ ...styles.carInfo, width: "100px", flexDirection: "column" }}>
              <Text style={styles.carTitle}>お見積書</Text>
              {(quotationDetail.car_image_base64 || quotationDetail.car_image_url) && (
                <Image
                  style={{ width: "75px" }}
                  src={quotationDetail.car_image_base64 || quotationDetail.car_image_url}
                />
              )}
            </View>
            {/* customer info */}
            <View style={{ display: "flex" }}>
              <View style={styles.infoRow}>
                <TableColPdf
                  styleBox={{ width: "111px" }}
                  title="見積番号"
                  content={quotationDetail.quote_no}
                />
                <TableColPdf
                  styleBox={{ width: "111px" }}
                  title="お客様氏名"
                  content={quotationDetail.customer_name}
                />
                <TableColPdf
                  styleBox={{ width: "111px" }}
                  title="作成日付"
                  content={formatDateTime(quotationDetail.created_at)}
                />
                <TableColPdf
                  styleBox={{ width: "111px" }}
                  title="有効期限"
                  content={formatDate(quotationDetail.exp_date)}
                />
              </View>
              <View style={{ ...styles.infoRow, marginTop: 4 }}>
                <TableColPdf
                  styleBox={{ width: "111px" }}
                  title="選択車両"
                  content={
                    hasTireData
                      ? `${quotationDetail.brnd_name} ${quotationDetail.modl_name} \n ${quotationDetail.modl_period_s}～${quotationDetail.modl_period_e}`
                      : ""
                  }
                />
                <TableColPdf
                  styleBox={{ width: "111px" }}
                  title="タイヤサイズ"
                  content={tireSizeRender}
                />
                <TableColPdf
                  styleBox={{ width: "111px" }}
                  title="ハブ径"
                  content={formatWheelRimDiamDisplay(quotationDetail.mmmy_mmm_hub1) || ""}
                />
              </View>
            </View>
          </View>
          {/* 税込表示（車両情報エリアの下辺・仕切り線の直上） */}
          <View style={{ alignItems: "flex-end", marginTop: 6 }}>
            <Text style={styles.footerText}>※金額は全て税込となります</Text>
          </View>
        </View>
        {/* Price per unit note */}
        <View style={styles.priceNote}>
          <Text style={styles.footerText}>※工賃表示価格は、1本あたりの金額となります。</Text>
        </View>
        {/* discount */}
        <View style={styles.containerDiscount}>
          <TableColPdf
            styleBoxTitle={{ backgroundColor: "#ace19d" }}
            title="交換工賃"
            content={!isPriceLabel(discReplac) ? discReplac : undefined}
          >
            {isPriceLabel(discReplac) ? renderPriceWithYen(discReplac) : null}
          </TableColPdf>
          <TableColPdf
            styleBoxTitle={{
              backgroundColor: !quotationDetail.waste_tire_cost_flag ? "#dfdfdf" : "#ace19d",
            }}
            title="処分"
            content={!isPriceLabel(discWaste) ? discWaste : undefined}
          >
            {isPriceLabel(discWaste) ? renderPriceWithYen(discWaste) : null}
          </TableColPdf>
          <TableColPdf
            styleBoxTitle={{
              backgroundColor: !quotationDetail.rubber_stem_valve_flag ? "#dfdfdf" : "#ace19d",
            }}
            title="エアバルブ"
            content={!isPriceLabel(discRubber) ? discRubber : undefined}
          >
            {isPriceLabel(discRubber) ? renderPriceWithYen(discRubber) : null}
          </TableColPdf>
          <TableColPdf
            styleBoxTitle={{
              backgroundColor: !quotationDetail.nitrogen_inflation_flag ? "#dfdfdf" : "#ace19d",
            }}
            title="窒素"
            content={!isPriceLabel(discNitrogen) ? discNitrogen : undefined}
          >
            {isPriceLabel(discNitrogen) ? renderPriceWithYen(discNitrogen) : null}
          </TableColPdf>
          <TableColPdf
            styleBoxTitle={{
              backgroundColor: !quotationDetail.hub_rust_prevention_flag ? "#dfdfdf" : "#ace19d",
            }}
            title="ハブ防錆"
            content={!isPriceLabel(discHub) ? discHub : undefined}
          >
            {isPriceLabel(discHub) ? renderPriceWithYen(discHub) : null}
          </TableColPdf>
          <TableColPdf
            styleBoxTitle={{ backgroundColor: "#dfdfdf" }}
            title="パンク補償"
            content={prunctureFlagRender(quotationDetail?.tire_puncture_flag)}
          />
        </View>
        {/* table est */}
        {quotationDetail.quote_type === "tire" &&
          quotationDetail.tbl_quote_detail_tire?.map((tire) => (
            <View wrap={false} key={tire.quote_detail_id}>
              <TableEstPreviewPdf quotation={tire as any} />
            </View>
          ))}
      </Page>
    </Document>
  );
}
